// =====================================================
// COMMENTS in the redline: the comments part holds the comments the body's marks refer to.
// =====================================================

import { tagCrossRelIds } from './xml-helpers';
import { NOTE_TEXT_NORM, fragmentText } from './story-text';
import { balanceCommentAnchors } from './anchors';
import type { ParsedDocument } from '../types';
import { addRelationship, declarePart, REL, CONTENT_TYPE } from './package-parts';


/** The parts a comment can be marked in. */
const ANCHOR_PARTS = /^word\/(document|footnotes|endnotes|header\d*|footer\d*)\.xml$/;
const COMMENT_RE = /<w:comment\b[^>]*?\bw:id="(\d+)"[\s\S]*?<\/w:comment>/g;

/** A package's comments: id -> the whole <w:comment> element. */
async function commentDefs(zip: any): Promise<Map<string, string>> {
  const map = new Map<string, string>();
  const xml: string | undefined = zip ? await zip.file('word/comments.xml')?.async('string') : undefined;
  if (!xml) return map;
  for (const m of xml.matchAll(COMMENT_RE)) if (!map.has(m[1])) map.set(m[1], m[0]);
  return map;
}

/** Whether two drafts' comments are the same comment: author, date, initials and text. */
const commentKey = (def: string) => {
  const open = (def.match(/^<w:comment\b[^>]*>/) || [''])[0];
  const attr = (n: string) => (open.match(new RegExp(`\\bw:${n}="([^"]*)"`)) || [])[1] || '';
  return [attr('author'), attr('date'), attr('initials'), NOTE_TEXT_NORM(fragmentText(def))].join('\u0001');
};

/**
 * Every comment mark in the redline given its comment, in one place. The revised draft's marks keep
 * their ids: their comments are the revised draft's, copied with its parts. A mark from the original
 * (tagged FRC-N as it was written: a deleted paragraph's) names the original's comment N, and:
 *
 * - when the revised draft has the same comment N (author, date, initials and text), it shares the
 *   revised draft's, or is dropped when the revised draft marks that comment in the redline too (a
 *   comment is marked once);
 * - when the revised draft has another comment N, or marks N, it gets an id of its own, with the
 *   original's comment copied under it (before, it showed the revised draft's comment N);
 * - otherwise it keeps N, and the original's comment is copied in.
 *
 * The comments part then holds exactly the comments the redline marks: a comment whose mark went (in
 * text a tracked deletion accepted, say) is dropped, with its entries in the linked parts. A comment
 * range missing its end or its reference is closed where it starts (balanceCommentAnchors).
 */
export async function resolveComments(newZip: any, origDocData: ParsedDocument, revDocData: ParsedDocument): Promise<void> {
  const parts = Object.keys(newZip.files).filter((n: string) => ANCHOR_PARTS.test(n))
    .sort((a: string, b: string) => (a === 'word/document.xml' ? -1 : b === 'word/document.xml' ? 1 : a.localeCompare(b)));
  const xmls = new Map<string, string>();
  for (const n of parts) xmls.set(n, await newZip.file(n).async('string'));
  const origDefs = await commentDefs(origDocData && origDocData.zip), revDefs = await commentDefs(revDocData && revDocData.zip);

  // 1. the original's marks
  const MARK = /<w:comment(?:RangeStart|RangeEnd|Reference)\b[^>]*?\bw:id="(FRC-)?(\d+)"[^>]*\/>/g;
  const revMarked = new Set<string>(), origIds: string[] = [];
  for (const x of xmls.values()) for (const m of x.matchAll(MARK)) {
    if (!m[1]) revMarked.add(m[2]);
    else if (!origIds.includes(m[2])) origIds.push(m[2]);
  }
  const imported = new Map<string, string>();   // id in the redline -> the original's comment under it
  if (origIds.length) {
    let max = 0;
    for (const id of [...revDefs.keys(), ...revMarked, ...origIds, ...origDefs.keys()]) max = Math.max(max, Number(id));
    const target = new Map<string, string | null>();
    for (const id of origIds) {
      const od = origDefs.get(id), rd = revDefs.get(id);
      if (od && rd && commentKey(od) === commentKey(rd)) target.set(id, revMarked.has(id) ? null : id);
      else if (!rd && !revMarked.has(id)) { target.set(id, id); if (od) imported.set(id, tagCrossRelIds(od)); }
      else {
        const nid = String(++max);
        target.set(id, nid);
        // (w14:paraId / textId must be unique per paragraph: the copy drops them)
        if (od) imported.set(nid, tagCrossRelIds(od).replace(/^(<w:comment\b[^>]*?\bw:id=")\d+"/, `$1${nid}"`)
          .replace(/\s+w14:(?:paraId|textId)="[0-9A-Fa-f]+"/g, ''));
      }
    }
    for (const [n, x] of xmls) {
      const y = x.replace(/<w:comment(?:RangeStart|RangeEnd|Reference)\b[^>]*?\bw:id="FRC-(\d+)"[^>]*\/>/g, (m: string, id: string) => {
        const t = target.get(id);
        return t == null ? '' : m.replace(`"FRC-${id}"`, `"${t}"`);
      });
      if (y !== x) { xmls.set(n, y); newZip.file(n, y); }
    }
  }

  // 2. the comments part: every comment a mark names
  const referencedIds = new Set<string>();
  for (const x of xmls.values()) {
    for (const m of x.matchAll(/<w:commentReference\s+[^>]*w:id="(\d+)"/g)) referencedIds.add(m[1]);
    for (const m of x.matchAll(/<w:commentRangeStart\s+[^>]*w:id="(\d+)"/g)) referencedIds.add(m[1]);
  }
  if (referencedIds.size > 0) {
    const defOf = (id: string) => imported.get(id) || revDefs.get(id) || (origDefs.get(id) ? tagCrossRelIds(origDefs.get(id)!) : null);
    const defOrPlaceholder = (id: string) =>
      defOf(id) ||
      `<w:comment w:id="${id}" w:author="Fredline" w:date="${new Date().toISOString().replace(/\.\d+Z$/, 'Z')}" w:initials=""><w:p/></w:comment>`;
    const existing: string | undefined = await newZip.file('word/comments.xml')?.async('string');
    if (existing) {
      // the revised draft's comments part: the comments it lacks added
      const defined = new Set<string>();
      for (const m of existing.matchAll(/<w:comment\b[^>]*?\bw:id="(\d+)"/g)) defined.add(m[1]);
      let inserts = '';
      for (const id of referencedIds) if (!defined.has(id)) inserts += defOrPlaceholder(id);
      if (inserts) newZip.file('word/comments.xml', existing.replace('</w:comments>', inserts + '</w:comments>'));
    } else {
      // no comments part: the original's (its root keeps every namespace its comments use), with
      // the comments the redline marks; a minimal one when the original had none either
      const origCommentsFile = origDocData && origDocData.zip && origDocData.zip.file('word/comments.xml');
      if (origCommentsFile) {
        // (relationship ids tagged: a hyperlink pasted in a comment comes with it, cross-rels.ts)
        let oc = tagCrossRelIds(await origCommentsFile.async('string')).replace(COMMENT_RE,
          (m: string, id: string) => (referencedIds.has(id) ? m : ''));
        const have = new Set([...oc.matchAll(/<w:comment\b[^>]*?\bw:id="(\d+)"/g)].map(m => m[1]));
        let inserts = '';
        for (const id of referencedIds) if (!have.has(id)) inserts += defOrPlaceholder(id);
        if (inserts) oc = oc.replace('</w:comments>', inserts + '</w:comments>');
        newZip.file('word/comments.xml', oc);
      } else {
        let inner = '';
        for (const id of referencedIds) inner += defOrPlaceholder(id);
        newZip.file('word/comments.xml',
          '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
          '<w:comments xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main">' +
          inner + '</w:comments>');
      }
      await declarePart(newZip, 'word/comments.xml', CONTENT_TYPE.comments);
      const rels: string | undefined = await newZip.file('word/_rels/document.xml.rels')?.async('string');
      if (rels && !/Target="comments\.xml"/.test(rels)) await addRelationship(newZip, 'word/_rels/document.xml.rels', { type: REL.comments, target: 'comments.xml' });
    }
  }

  // 3. each range with its end and reference
  const body: string | undefined = await newZip.file('word/document.xml')?.async('string');
  if (body != null) { const balanced = balanceCommentAnchors(body); if (balanced !== body) newZip.file('word/document.xml', balanced); }

  // 4. no comment the redline does not mark
  await pruneOrphanComments(newZip);
}

async function pruneOrphanComments(newZip: any): Promise<void> {
  // ===== PRUNE ORPHANED COMMENT DEFINITIONS =====
  // A comment loses its anchor when the text it annotated sits inside an accepted
  // tracked deletion (flattenAcceptRevisions removes the in-deletion markers). The
  // definition then lingers in comments.xml with nothing pointing to it — a phantom
  // comment in the reviewer's pane. Word tolerates it, but it's clutter. Drop any
  // comment with no commentReference AND no commentRangeStart in the final body, and
  // keep the linked commentsExtended / commentsIds / commentsExtensible parts in sync
  // (comment id → its paragraph w14:paraId → durableId).
  {
    const commentsFile = newZip.file('word/comments.xml');
    if (commentsFile) {
      const usedIds = new Set<string>();
      for (const n of Object.keys(newZip.files).filter((n: string) => ANCHOR_PARTS.test(n))) {
        const x: string = await newZip.file(n).async('string');
        for (const m of x.matchAll(/<w:commentReference\b[^>]*\bw:id="(\d+)"/g)) usedIds.add(m[1]);
        for (const m of x.matchAll(/<w:commentRangeStart\b[^>]*\bw:id="(\d+)"/g)) usedIds.add(m[1]);
      }

      const commentsXml = await commentsFile.async('string');
      const prunedParaIds = new Set<string>();
      const newCommentsXml = commentsXml.replace(
        /<w:comment\b[^>]*?\bw:id="(\d+)"[\s\S]*?<\/w:comment>/g,
        (block: string, id: string) => {
          if (usedIds.has(id)) return block;
          for (const pm of block.matchAll(/\bw14:paraId="([0-9A-Fa-f]+)"/g)) prunedParaIds.add(pm[1]);
          return '';
        });

      if (newCommentsXml !== commentsXml) {
        newZip.file('word/comments.xml', newCommentsXml);

        // commentsExtended: <w15:commentEx w15:paraId=.. /> keyed by the comment paraId.
        const exFile = newZip.file('word/commentsExtended.xml');
        if (exFile && prunedParaIds.size) {
          const ex = await exFile.async('string');
          newZip.file('word/commentsExtended.xml', ex.replace(/<\w+:commentEx\b[^>]*\/>/g,
            (el: string) => { const pm = el.match(/\bparaId="([0-9A-Fa-f]+)"/); return pm && prunedParaIds.has(pm[1]) ? '' : el; }));
        }

        // commentsIds: <w16cid:commentId paraId=.. durableId=.. /> — drop pruned, collect durableIds.
        const prunedDurable = new Set<string>();
        const idsFile = newZip.file('word/commentsIds.xml');
        if (idsFile && prunedParaIds.size) {
          const ids = await idsFile.async('string');
          newZip.file('word/commentsIds.xml', ids.replace(/<\w+:commentId\b[^>]*\/>/g,
            (el: string) => {
              const pm = el.match(/\bparaId="([0-9A-Fa-f]+)"/);
              if (pm && prunedParaIds.has(pm[1])) { const dm = el.match(/\bdurableId="([^"]+)"/); if (dm) prunedDurable.add(dm[1]); return ''; }
              return el;
            }));
        }

        // commentsExtensible: <w16cex:commentExtensible durableId=.. /> keyed by durableId.
        const cexFile = newZip.file('word/commentsExtensible.xml');
        if (cexFile && prunedDurable.size) {
          const cex = await cexFile.async('string');
          newZip.file('word/commentsExtensible.xml', cex.replace(/<\w+:commentExtensible\b[^>]*\/>/g,
            (el: string) => { const dm = el.match(/\bdurableId="([^"]+)"/); return dm && prunedDurable.has(dm[1]) ? '' : el; }));
        }
      }
    }
  }
}
